import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { homeSummaryQuery } from '@/shared/api/queries/home';
import { programFullQuery } from '@/shared/api/queries/program';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { LoadingStatus } from '@/shared/ui/spinner';

import { useBootstrap } from '../api/use-bootstrap';
import { useSignOutAction } from '../use-sign-out-action';

import type { ReactNode } from 'react';

export function LearnerBootstrap({
  learnerId,
  signOut,
  children,
}: {
  learnerId: string | null;
  signOut: () => Promise<void>;
  children: ReactNode;
}) {
  const setup = useBootstrap(learnerId);
  const logout = useSignOutAction(signOut);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!setup.data) return;
    void queryClient.prefetchQuery(programFullQuery);
    void queryClient.prefetchQuery(homeSummaryQuery);
  }, [setup.data, queryClient]);
  if (learnerId === null || setup.data) return children;
  return (
    <main className="bg-canvas text-fg flex min-h-screen items-center justify-center px-6">
      <section className="w-full max-w-md space-y-4" aria-labelledby="setup-title">
        <h1 id="setup-title" className="text-xl font-medium">
          Preparing your residency
        </h1>
        {setup.error ? (
          <>
            <Alert>We could not finish setting up your program. {setup.error.message}</Alert>
            <div className="flex gap-2">
              <Button onClick={() => void setup.refetch()} loading={setup.isFetching}>
                Try again
              </Button>
              <Button variant="ghost" loading={logout.busy} onClick={() => void logout.run()}>
                Sign out
              </Button>
            </div>
            {logout.error ? (
              <Alert>
                {logout.error}{' '}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => void logout.force()}
                >
                  Sign out anyway
                </button>
              </Alert>
            ) : null}
          </>
        ) : (
          <div className="space-y-5">
            <LoadingStatus label="Getting your program ready…" />
            <ProgressBar value={null} label="Setting up your program" />
          </div>
        )}
      </section>
    </main>
  );
}
